/**
 * Field filter_config helpers (ORCH-400 §3.5, §4.3)
 *
 * Purpose: The Field modal's "Filtered by" section edits `fields.filter_config`, which
 *          stores exclusions, not inclusions, so a related field added later filters
 *          by default. These helpers read and write that shape; the default (every
 *          source on) is stored as null.
 */

import type { FieldFilterConfig, PickerFilterSource } from '../../../services/metadataService';

type SourceKey = Pick<PickerFilterSource, 'model_id' | 'fielddef_id'>;

const sameSource = (a: SourceKey, b: SourceKey): boolean =>
  a.model_id === b.model_id && a.fielddef_id === b.fielddef_id;

/** Whether the Field's related/subset filtering is on (the default) */
export const isFilterEnabled = (config: FieldFilterConfig | null | undefined): boolean =>
  config?.enabled !== false;

/** Whether one candidate source is excluded by the config */
export const isSourceExcluded = (
  config: FieldFilterConfig | null | undefined,
  source: SourceKey
): boolean => (config?.exclude ?? []).some((entry) => sameSource(entry, source));

/** The config with the master switch set; null when it is back at the default */
export const withFilterEnabled = (
  config: FieldFilterConfig | null | undefined,
  enabled: boolean
): FieldFilterConfig | null => normalizeFilterConfig({ ...(config ?? {}), enabled });

/** The config with one source excluded or restored; null when back at the default */
export const withSourceExcluded = (
  config: FieldFilterConfig | null | undefined,
  source: SourceKey,
  excluded: boolean
): FieldFilterConfig | null => {
  const rest = (config?.exclude ?? []).filter((entry) => !sameSource(entry, source));
  const exclude = excluded
    ? [...rest, { model_id: source.model_id, fielddef_id: source.fielddef_id }]
    : rest;
  return normalizeFilterConfig({ ...(config ?? {}), exclude });
};

/**
 * The stored shape: `enabled` only when false, `exclude` only when non-empty, and null
 * when neither is set, so a Field at the default carries no config at all.
 */
export const normalizeFilterConfig = (
  config: FieldFilterConfig | null | undefined
): FieldFilterConfig | null => {
  if (!config) return null;
  const out: FieldFilterConfig = {};
  if (config.enabled === false) out.enabled = false;
  if (config.exclude && config.exclude.length > 0) out.exclude = config.exclude;
  return Object.keys(out).length ? out : null;
};
